import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ClipboardList, Check, X, Loader2, Gamepad2 } from "lucide-react";

export const ModderApplications = () => {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/modder-applications");
      setApps(data);
    } catch { /* not admin */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (user?.role !== "admin") return null;

  const review = async (app, status) => {
    setBusy(app.id);
    try {
      const { data } = await api.patch(`/modder-applications/${app.id}`, { status });
      setApps((a) => a.map((x) => (x.id === data.id ? data : x)));
      toast.success(status === "approved" ? `${app.name} is now a modder!` : "Application rejected.");
    } catch { toast.error("Action failed."); }
    finally { setBusy(""); }
  };

  const pending = apps.filter((a) => a.status === "pending");

  return (
    <div className="animate-fade-up mb-8 rounded-2xl border border-[#00F0FF]/30 bg-[#0F1424] p-6" data-testid="modder-applications">
      <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-white">
        <ClipboardList size={18} className="text-[#00F0FF]" /> Modder Applications
        {pending.length > 0 && <span className="rounded-full bg-[#FFB800] px-2 py-0.5 text-xs font-bold text-[#090C15]">{pending.length} new</span>}
      </h2>
      <p className="mt-1 text-sm text-[#94A3B8]">Approve trusted applicants to auto-promote them to moderator.</p>

      {loading ? (
        <div className="flex justify-center py-8 text-[#64748B]"><Loader2 className="animate-spin" /></div>
      ) : apps.length === 0 ? (
        <p className="py-6 text-center text-sm text-[#64748B]" data-testid="no-applications">No applications yet.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {apps.map((a) => (
            <div key={a.id} data-testid={`application-${a.id}`} className="rounded-lg border border-[#1E293F] bg-[#141B2D] p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-white">{a.name}</span>
                    <span className="text-xs text-[#64748B]">{a.email}</span>
                    {a.status !== "pending" && (
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${a.status === "approved" ? "bg-[#00FF87]/15 text-[#00FF87]" : "bg-[#FF2A55]/15 text-[#FF2A55]"}`}>{a.status}</span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-[#94A3B8]"><Gamepad2 size={12} /> {a.gamertag} · <span className="text-[#00F0FF]">{a.platform}</span></div>
                </div>
                {a.status === "pending" && (
                  <div className="flex gap-2">
                    <Button data-testid={`approve-app-${a.id}`} size="sm" disabled={busy === a.id} onClick={() => review(a, "approved")} className="bg-[#00FF87]/20 font-semibold text-[#00FF87] hover:bg-[#00FF87]/30">
                      <Check size={14} className="mr-1" /> Approve
                    </Button>
                    <Button data-testid={`reject-app-${a.id}`} size="sm" variant="outline" disabled={busy === a.id} onClick={() => review(a, "rejected")} className="border-[#1E293F] bg-[#0F1424] text-[#94A3B8] hover:bg-[#1A233A] hover:text-white">
                      <X size={14} className="mr-1" /> Reject
                    </Button>
                  </div>
                )}
              </div>
              {a.experience && <p className="mt-2 text-sm text-[#94A3B8]"><span className="text-[#64748B]">Experience:</span> {a.experience}</p>}
              <p className="mt-1 text-sm text-[#CBD5E1]"><span className="text-[#64748B]">Why:</span> {a.reason}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
